/**
 * Per-screen help content registry.
 *
 * Keys are route paths (what `react-router` gives you via `useLocation().pathname`).
 * Content is plain strings — translate to JSX in the `<HelpPanel>` consumer.
 *
 * Key phrases in `tips` and guide `steps` are wrapped in `**...**` so the
 * panel can render them bold. Apply to 2–4 phrases per tip that matter most.
 *
 * Each entry optionally varies by role tier; the lookup returns the most
 * specific match. Ship new screens by appending to this file; no other edits
 * are needed.
 */

import { BOOKING_STATE_DIAGRAM } from '@/core/models/booking'
import { PRICING_PRECEDENCE_DIAGRAM } from '@/core/models/court'
import { SubscriptionPricing } from '@/core/models/subscription'

export interface HelpGuide {
  title: string
  steps: string[]
  /** If set, the panel shows a CTA that navigates here. */
  screenTarget?: string
  /** Optional override for the CTA label. */
  ctaLabel?: string
}

/**
 * Mermaid diagram embedded in a help entry. Used for state machines,
 * decision trees, and precedence flows that read poorly as prose.
 * Source is authored as a string next to the related domain / model
 * file (e.g. `BOOKING_STATE_DIAGRAM` next to `BOOKING_STATUS_TRANSITIONS`)
 * so the code + diagram stay in sync.
 */
export interface HelpDiagram {
  /** Mermaid source (stateDiagram-v2, flowchart, erDiagram, etc.). */
  source: string
  /** Short label rendered above the diagram. */
  caption?: string
}

/**
 * A labelled cluster of tips. Screens with more than a handful of tips
 * group them under short section labels (e.g. "Mapa de pistas", "Cobros")
 * so the help panel reads as scannable sections instead of one long list.
 * Small screens (≤4 tips, single topic) can keep the flat `tips` array,
 * which renders under a single "Consejos" header — both are supported so
 * we only group where grouping earns its keep.
 */
export interface HelpTipSection {
  /** Short section header shown above this cluster (Sentence case). */
  label: string
  tips: string[]
}

export interface HelpPage {
  /**
   * Identifier matching a screen-level tab (typically the `?tab=` query
   * value). When present and equal to the active tab, `<HelpPanel>`
   * renders only that page — no tab strip — so the side panel mirrors
   * what the user is actually looking at.
   */
  id?: string
  label: string
  description: string
  tips?: string[]
  /** Grouped alternative to `tips`; use for pages with many, multi-topic tips. */
  sections?: HelpTipSection[]
  guides?: HelpGuide[]
  diagrams?: HelpDiagram[]
}

export interface HelpContent {
  title: string
  description?: string
  tips?: string[]
  /** Grouped alternative to `tips`; use for screens with many, multi-topic tips. */
  sections?: HelpTipSection[]
  guides?: HelpGuide[]
  diagrams?: HelpDiagram[]
  /** Multi-tab help for screens with distinct audiences (e.g. owner vs staff). */
  pages?: HelpPage[]
}

export type HelpRoleTier = 'owner' | 'manager' | 'staff'

type HelpEntry = HelpContent | ((tier: HelpRoleTier | null) => HelpContent)

const REGISTRY: Record<string, HelpEntry> = {
  '/': (tier) => {
    const isStaff = tier === 'staff'
    const sharedSections = [
      {
        label: 'Cifras del día',
        tips: [
          'Los **KPIs** de la fila superior se actualizan en tiempo real sin recargar. **Ocupación hoy** mide minutos jugados ÷ minutos abiertos del día.',
        ],
      },
      {
        label: 'Mapa de pistas',
        tips: [
          'El **Mapa de pistas** muestra de 06:00 a 02:00 del día siguiente — los partidos overnight se ven sin scroll.',
          'Las **franjas rayadas** son fuera del horario de apertura. Aún puedes reservar — un diálogo te pide confirmación antes.',
          'Si el día tiene **horario distinto** (festivo, torneo) verás un **Día especial** centrado en la cabecera del Mapa.',
          'Cada reserva muestra **el primer jugador** o "**Anónimo**" si no hay nombre. El roster completo está en el detalle.',
          'Cuando alguien reserva para un grupo aparece **"X reserva para Y + N amigos"** — el primero paga; los demás son la roster.',
          'Pulsa el icono **(i)** junto a "Mapa de pistas" para ver la **leyenda** de colores y tipos de reserva.',
          'Los **colores de pago** son: verde (pagada), naranja (parcial), rojo (pendiente), gris (reembolsada), azul (gratuita).',
        ],
      },
      {
        label: 'Cobros, tareas y actividad',
        tips: [
          'En **Por cobrar** los puntos de pago permiten **registrar el cobro inline** (efectivo / tarjeta) sin abrir el detalle.',
          'Pulsa **Canceladas** para ver las reservas anuladas en su sitio — aparecen en gris para mantener el contexto visual.',
          'Bajo el Mapa de pistas, **Última actividad** muestra los últimos cambios del club (reservas, cobros, personal, ajustes). Pulsa **Ver todo** para abrir el registro completo en Actividad.',
        ],
      },
      {
        label: 'App y sin conexión',
        tips: [
          'El botón flotante de **Novedades** (esquina inferior derecha) muestra los pasos de configuración pendientes para tu rol —incluido cómo **instalar la app** en escritorio—; desaparece cuando ya no quedan.',
          'Si la WiFi cae, aparece la barra **Sin conexión**: el calendario, las **reservas nuevas**, los cobros y el CRM siguen funcionando con los datos guardados. Todo se sincroniza al reconectar.',
        ],
      },
    ]
    if (isStaff) {
      return {
        title: 'Hoy',
        description:
          'Tu panel del día: KPIs en vivo, el mapa de pistas en curso y la cola de **Acciones pendientes** para presentar y cobrar sin abrir cada reserva.',
        sections: [
          ...sharedSections,
          {
            label: 'Acciones pendientes',
            tips: [
              '**Acciones pendientes** lista reservas que requieren acción. Cada fila muestra un botón principal contextual: **Presentar** si el check-in está pendiente, **Cobrar** si ya están en pista pero el pago sigue abierto.',
              'Junto al botón principal hay iconos de acción rápida: **Ausente** (bandera) para registrar no-show, **Cobrar** con importe si hay pago pendiente además del check-in, y los iconos de **llamada / WhatsApp** para contactar al jugador.',
              'Si hay 2+ reservas sin check-in en la misma hora, usa **Presentar todos** en la cabecera del panel para marcarlas en una sola acción (con deshacer).',
              'El KPI **Pago pendiente** resume cuánto queda por cobrar hoy; las reservas que lo componen están en **Acciones pendientes**, listas para cobrar.',
            ],
          },
        ],
        guides: [
          {
            title: 'Cómo gestionar reservas pendientes',
            screenTarget: '/',
            steps: [
              'Revisa la sección "**Acciones pendientes**".',
              'Pulsa **Presentar** (botón principal verde) junto a cada reserva para marcar que los jugadores llegaron.',
              'Si no aparecen, pulsa el icono de **bandera (Ausente)** para registrar el no-show.',
              'Si el pago también está pendiente, verás el importe junto al icono de tarjeta — pulsa para cobrar sin salir del panel.',
            ],
          },
          {
            title: 'Cómo crear una reserva walk-in',
            steps: [
              'Toca un **slot vacío** en el Mapa de pistas — se abre el formulario pre-relleno con pista y hora.',
              'Si la franja está cerrada, **confirma** que quieres reservar fuera de horario.',
              'Escribe el nombre del jugador y pulsa "**Guardar**".',
            ],
          },
          {
            title: 'Mover o ampliar una reserva existente',
            steps: [
              'Arrastra una **reserva** a otra hora o pista para moverla.',
              'Arrastra el **borde derecho** para alargar o acortar la duración.',
              'Si la sueltas fuera del horario habitual verás un aviso — la reserva se guarda igualmente.',
            ],
          },
        ],
      }
    }
    return {
      title: 'Hoy',
      description:
        'El panel de operaciones del día: las cifras en vivo, el mapa de pistas, la cola de **Acciones pendientes** y la última actividad del club. Para tendencias semanales y analítica, usa **Rendimiento**.',
      sections: [
        ...sharedSections,
        {
          label: 'Tu primer día como propietario',
          tips: [
            'Durante la prueba, **Hoy** muestra el nombre del club, la fecha real de fin y tres pasos: confirma pistas, horario y precios después de revisarlos. Visitar una pantalla no confirma sus valores.',
            'Al confirmar lo esencial, **Crear mi primera reserva** abre el calendario real. Las reservas online empiezan desactivadas; catálogo, equipo e instalación pueden esperar.',
          ],
        },
        {
          label: 'Acciones pendientes e ingresos',
          tips: [
            '**Acciones pendientes** agrupa reservas con check-in o pago pendiente. Cada fila muestra el botón más urgente: **Presentar** si falta check-in, **Cobrar** si ya están en pista. Los iconos de acción rápida permiten gestionar el resto sin abrir el detalle.',
            'El KPI **Pago pendiente** resume lo que queda por cobrar hoy; la cola accionable vive en **Acciones pendientes**.',
            'El **mini-gráfico** junto a **Ingresos** es la tendencia visual de los últimos 7 días — sólo contexto, no es interactivo. Para el detalle semana a semana abre **Rendimiento** en el menú.',
          ],
        },
      ],
      guides: [
        {
          title: 'Cómo confirmar reservas pendientes',
          steps: [
            'Revisa la sección "**Acciones pendientes**".',
            'Pulsa **Presentar** (botón principal) para confirmar que los jugadores llegaron.',
            'Si no aparecen, pulsa el icono de **bandera (Ausente)** para registrar el no-show.',
            'Si el pago también está pendiente, el importe aparece junto al icono de tarjeta — pulsa para cobrar.',
          ],
        },
        {
          title: 'Para ver tendencias semanales',
          screenTarget: '/analytics',
          ctaLabel: 'Ir a Rendimiento',
          steps: [
            'Ve a **Rendimiento** en el menú lateral.',
            'Cambia la **ventana temporal** para ver semana, mes o rango personalizado.',
          ],
        },
      ],
    }
  },

  '/ventas': {
    title: 'Ventas',
    description:
      'Punto de venta del mostrador (bolas, grip, bebida, ropa…) y registro histórico de operaciones. Cobra una venta desde aquí, desde la **barra de acciones** (abajo, centrada) o con **⌘K → Nueva venta** desde cualquier pantalla.',
    sections: [
      {
        label: 'Registrar una venta',
        tips: [
          'Toca **Nueva venta** para abrir el panel de cobro. Pulsa un producto del catálogo para añadirlo al **Carrito** (anclado en la parte inferior). Con más de 4 artículos el carrito se colapsa en una línea resumen — toca la cabecera para desplegarlo.',
          'Asocia un **cliente** del CRM si quieres que la venta sume a su **Gastado** total — deja el campo libre o vacío para una venta anónima.',
          'Cambia el método de pago en la pestaña inferior (Efectivo / Tarjeta / Bizum / Transferencia / Otro). Pulsa **Guardar sin cobrar** si la cobranza queda para más tarde.',
        ],
      },
      {
        label: 'Histórico y filtros',
        tips: [
          'El **histórico** lista todas las ventas del día por defecto. Filtra por **categoría**, **método**, **estado** o rango de **fechas** para auditar un cierre.',
        ],
      },
      {
        label: 'Catálogo y reservas',
        tips: [
          'Para gestionar el catálogo (añadir productos, renombrar categorías) ve a **Catálogo** en el menú lateral.',
          'Una venta vinculada a una reserva (match upsell) hereda el `customerId` del cliente principal de esa reserva.',
        ],
      },
    ],
  },

  '/catalogo': {
    title: 'Catálogo',
    description:
      'Lo que vendes en el mostrador organizado por categorías, con **hasta dos niveles** (p. ej. Material ▸ Bolas). Los artículos son **Productos** (bolas, agua, ropa — el cliente se lo lleva) o **Servicios** (encordado, clase, alquiler de pala, luz — intangibles). **No es inventario**: no controla stock — son atajos de cobro con un precio por defecto que puedes ajustar en cada venta.',
    sections: [
      {
        label: 'Añadir y organizar',
        tips: [
          'Pulsa **Añadir** para crear un artículo. Elige **Tipo** (Producto o Servicio) en el panel. Gestiona las **categorías** desde el botón **Categorías**.',
          '¿Catálogo vacío o te falta lo típico? Pulsa **Recomendados** y añadimos material, bebidas, comida, ropa y servicios habituales (luz, alquileres, clase…). Solo **añade lo que falta**: no toca ni borra lo que ya tienes.',
          'Borrar una categoría **no borra sus artículos** — pasan a «Sin categoría». Si borras una **categoría padre**, sus subcategorías van con ella.',
        ],
      },
      {
        label: 'Precio y servicios en reservas',
        tips: [
          'Los **Servicios** marcados como «Se puede añadir a una reserva» aparecen en el selector **«Servicio»** del formulario de reserva. Elige también el **tipo de línea** (Luz, Calefacción, Alquiler de pala…) para que los análisis categoricen bien el ingreso.',
          'El **precio es orientativo**: en la venta puedes tocar el importe de la línea y cobrarlo distinto, sin tocar el catálogo.',
        ],
      },
      {
        label: 'Buscar y editar',
        tips: [
          'Usa el filtro **Tipo** (Producto / Servicio) y **Categoría** para encontrar artículos rápido; **toca una fila** para editarla o eliminarla.',
          'Editar o borrar un artículo **no cambia ventas ya registradas** (se copian al cobrar).',
        ],
      },
      {
        label: 'Uso en ventas y mostrador',
        tips: [
          'El **mostrador** usa este catálogo: cada artículo es un botón, agrupado por categoría, con **"+ Otro"** siempre a mano. Cobra desde cualquier pantalla con la **barra de acciones** (abajo, centrada) o **⌘K → Nueva venta**.',
        ],
      },
    ],
  },

  '/calendar': {
    title: 'Calendario',
    description:
      'Vista de día: una columna por pista, una fila por franja horaria. La **tira semanal** arriba muestra ocupación real de cada día.',
    sections: [
      {
        label: 'Vista de la rejilla',
        tips: [
          'La rejilla muestra de **06:00 a 02:00** del día siguiente — los partidos overnight se ven sin scroll horizontal.',
          'Las **franjas rayadas** quedan fuera del horario de apertura ese día. Aún puedes reservar — pedimos confirmación primero.',
          'Si el día tiene **horario especial** (festivo, torneo) lo verás centrado en la cabecera junto al título "Mapa de pistas".',
          'Pulsa **(i)** en la cabecera del **Mapa de pistas** para ver la **leyenda** de colores (estado, pago, tipo).',
          'Pulsa **Canceladas** para ver las reservas anuladas en su sitio — aparecen en gris para mantener el contexto visual.',
        ],
      },
      {
        label: 'Tira semanal y filtros',
        tips: [
          'La **tira semanal** mide ocupación real (minutos reservados ÷ minutos abiertos), así un sábado largo no compite con un lunes corto.',
          'Filtros **Tipo / Estado / Pista**, **Buscar jugador** y —si el club tiene el **Rol Entrenador** activo— **Entrenador** (para ver solo las clases de un profesor) afectan tanto al grid como al porcentaje de la tira semanal.',
        ],
      },
      {
        label: 'Crear y mover reservas',
        tips: [
          'Toca un **slot vacío** para crear una reserva pre-rellena con pista y hora.',
          'Arrastra una reserva a otra hora/pista para **mover**; arrastra el borde derecho para **alargar**.',
        ],
      },
    ],
    guides: [
      {
        title: 'Cómo crear una reserva desde el calendario',
        screenTarget: '/calendar',
        steps: [
          'Navega al día deseado con las **flechas < >** o toca un día en la tira semanal.',
          'Toca el **slot vacío** en la columna de la pista y hora que quieras.',
          'Si está fuera de horario, **confirma** que quieres reservar igualmente.',
          'Rellena los detalles en el formulario y pulsa "**Guardar**".',
        ],
      },
      {
        title: 'Cómo encontrar la reserva de un jugador',
        steps: [
          'Escribe el nombre, teléfono o email en **Buscar jugador**.',
          'La rejilla mantiene visible solo las celdas que coinciden.',
          'Toca cualquier coincidencia para abrir su detalle.',
        ],
      },
      {
        title: 'Mover o ampliar una reserva',
        steps: [
          'Arrastra una **reserva** a otra hora o pista para moverla.',
          'Arrastra el **borde derecho** para alargar o acortar la duración.',
          'Si cae en una franja cerrada verás un aviso, pero la reserva se guarda.',
        ],
      },
    ],
  },

  '/bookings': {
    title: 'Reservas',
    description:
      'Lista de **reservas** filtrable por rango de fechas, tipo, estado, método de pago y pista. Crea, edita y cancela desde aquí.',
    pages: [
      {
        label: 'Gestión',
        description:
          'Lista completa de **reservas**. Filtra por estado, pago o pista para encontrar lo que buscas.',
        sections: [
          {
            label: 'Crear y filtrar',
            tips: [
              'Pulsa **Reservar** (esquina superior) para crear una reserva nueva.',
              'Cada reserva tiene un **tipo**: Partido, **Clase** (con **entrenador** y nivel si el club activó el Rol Entrenador), Torneo o Bloqueo.',
              'Filtra por **tipo, estado, pago o pista** con las píldoras sobre la lista.',
            ],
          },
          {
            label: 'Información de contacto y roster',
            tips: [
              'En vista escritorio (≥1280 px) la columna **Teléfono** muestra el contacto principal de cada reserva — pulsa el número para llamar sin abrir el detalle.',
              'Si la reserva la hizo otra persona, aparece **"X reserva para Y + N amigos"** — el primero es el responsable del pago, los demás son la roster del partido.',
            ],
          },
          {
            label: 'Cancelación y lote',
            tips: [
              'Las **canceladas** quedan en el historial pero no cuentan en los KPIs.',
              'Selecciona varias reservas con los checkboxes para **cancelar en lote**.',
            ],
          },
          {
            label: 'Cobros y ventas',
            tips: [
              'El botón **Cobrar** de cada fila registra el pago sin abrir el detalle — efectivo, tarjeta, transferencia o mixto, total o parcial.',
              'Si el club tiene el **Monedero** activo, al cobrar puedes usar el **saldo** o un **bono** del jugador como método de pago, además de efectivo, tarjeta o transferencia.',
              'Dentro del detalle, la sección **Ventas** (bajo Pago) lista las bolas, grip o material vendidos en esa reserva y permite añadir más — se cobran junto con la pista o por separado.',
            ],
          },
        ],
        guides: [
          {
            title: 'Cómo crear una reserva manual',
            steps: [
              'Pulsa **Reservar** en la esquina superior derecha.',
              'Selecciona la **pista y el horario** deseado.',
              'Escribe el nombre del jugador (o búscalo si ya existe).',
              'Pulsa "**Guardar**" para confirmar la reserva.',
            ],
          },
          {
            title: 'Cómo cancelar una reserva',
            steps: [
              'Toca la reserva en la lista para abrir sus **detalles**.',
              'Pulsa "**Cancelar reserva**" y confirma.',
              'Si la reserva tiene **dinero cobrado**, el diálogo ofrece **devolver el total** (por defecto) o **dejar como penalización** (sin reembolso). Si el club tiene el **Monedero** activo puedes elegir devolver al **Monedero** en vez de al método original; una reserva pagada con **bono** recupera la **sesión**, no efectivo. La cancelación sólo se aplica si el reembolso se registra bien — así no queda dinero atrapado.',
            ],
          },
          {
            title: 'Cómo eliminar (no cancelar) una reserva',
            steps: [
              'Sólo los roles con permiso ven la opción **Eliminar reserva** dentro del detalle — por defecto Propietario y Gerente; configurable en **Ajustes → Reglas**.',
              'Eliminar borra la reserva del histórico — usar para reservas creadas por error (ej. doble click). **No reembolsa automáticamente**: si tenía pagos cobrados, los moverá a "Pagos huérfanos" para que reembolses a mano antes de cerrar la operación.',
              'Las reservas con más de **24 horas de antigüedad** no se pueden eliminar — se cancelan. La eliminación no deja rastro; la cancelación sí.',
            ],
          },
          {
            title: 'Cómo añadir una venta a una reserva',
            steps: [
              'Abre la reserva y despliega la sección **Ventas** (bajo Pago).',
              'Pulsa "**+ Venta**", toca un preset o escribe en la línea abierta (la siguiente se abre sola).',
              'Cobra ahora o guarda sin cobrar — aparece en la reserva y en Ingresos.',
            ],
          },
        ],
      },
      {
        label: 'Estados',
        description: 'Cada reserva pasa por **estados** que reflejan su ciclo de vida.',
        tips: [
          '**Confirmada**: pista bloqueada. Estado por defecto al crear.',
          '**Presentada**: los jugadores llegaron al club. Marca con ✓ en Acciones pendientes.',
          '**No presentado**: pasó la hora sin asistencia. Afecta al historial del jugador. Se puede revertir con **Restaurar** si el jugador llega tarde — vuelve a Confirmada.',
          '**Cancelada**: no ocurrirá. La pista queda libre; el registro se conserva. Las canceladas quedan en el historial pero no cuentan en los KPIs — también aparecen en gris en el calendario para mantener el contexto. Pulsa una cancelada para crear una **nueva reserva en ese hueco** con **Reservar**.',
        ],
        diagrams: [
          {
            caption: 'Estados de una reserva',
            source: BOOKING_STATE_DIAGRAM,
          },
        ],
      },
    ],
  },

  '/courts': {
    title: 'Pistas',
    description:
      'Gestiona tus **pistas**: añade, edita o desactívalas. Una pista inactiva no aparece en el calendario.',
    sections: [
      {
        label: 'Editar pista',
        tips: [
          'Toca una pista para **editar** nombre, tipo, superficie, deporte o número de plazas.',
          'El campo **Plazas** fija explícitamente el aforo (p.ej. 4 para una pista normal de pádel). Si se deja vacío, se usa el aforo por defecto según el tamaño.',
        ],
      },
      {
        label: 'Precios',
        tips: [
          'Pulsa el **precio base** en la fila para cambiarlo sin abrir la pista. Las reglas pico se mantienen.',
          'El precio se muestra como **€X / 90 min** (lo que cuesta una reserva) o **€X / hora**, con un distintivo: **«General»** = usa el precio base del club · **«Personalizada»** = la pista tiene su propia regla. Ajusta el base en **Ajustes → Precios** o abre la pista para fijar uno propio.',
          'Para configurar reglas pico (franjas horarias, fines de semana) ve a **Ajustes → Precios**.',
        ],
      },
      {
        label: 'Activar y desactivar',
        tips: [
          '**Desactivar** una pista no borra su historial de reservas.',
        ],
      },
    ],
    guides: [
      {
        title: 'Cómo añadir una pista',
        steps: [
          'Pulsa "**Añadir pista**" en la parte superior.',
          'Escribe el nombre (ej. "Pista 1") y selecciona tipo y superficie.',
          'Pulsa "**Guardar**". La pista aparece en el calendario.',
        ],
      },
      {
        title: 'Cómo desactivar una pista',
        steps: [
          'Toca la pista que quieras desactivar.',
          'Desactiva el interruptor "**Activa**".',
          'La pista deja de aparecer en el calendario; el **historial se conserva**.',
        ],
      },
    ],
  },

  '/members': {
    title: 'Clientes',
    description:
      'Base de datos de tus **clientes** (personas y grupos), construida automáticamente al crear reservas. Busca, exporta y consulta la **ficha** de cada uno con su historial, nivel y fiabilidad.',
    sections: [
      {
        label: 'Buscar y exportar',
        tips: [
          'Busca por **nombre, teléfono o email**.',
          'Filtra entre **Personas** y **Grupos** (ej. "Equipo López", "Clase 18h") cuando una distinción importa.',
          'Los datos son tuyos: **exportables** a CSV en cualquier momento.',
        ],
      },
      {
        label: 'Ficha del cliente',
        tips: [
          'Toca un cliente para ver su ficha con **historial de reservas**, su sección **Ventas** (bolas, grip o material que le has cobrado) y estadísticas.',
          'El **Gastado** de cada cliente suma sus reservas y sus ventas en mostrador. La lista muestra también cuántas **ventas** tiene (así un cliente con "0 reservas · 1 venta" ya no parece un gasto sin origen), y su ficha desglosa el Gastado en **Reservas** y **Ventas**. Al cobrar una venta, elige el cliente en el campo **Cliente** para que cuente en su ficha (o déjalo libre si es anónima).',
          'La lista muestra **Edad** y **Estilo** (Mano + Posición) para personas; los grupos las ocultan porque no aplican.',
          'Si activas el **Monedero** (opción dentro de **Clientes → Configurar**), la ficha incluye la sección **Monedero**: el saldo prepago del jugador, sus **bonos** de sesiones y el histórico de movimientos. Desde ahí puedes **recargar** saldo, **vender un bono** o hacer un **ajuste** con motivo.',
        ],
      },
      {
        label: 'Perfil de juego y nivel',
        tips: [
          'Asigna **Nivel** (escala 0–7), **Mano** y **Posición preferida** a personas para componer mejores partidos.',
          'Filtra y ordena por **Nivel** desde la cabecera de la columna. Usa los botones de banda o introduce un valor numérico preciso.',
        ],
      },
      {
        label: 'Fiabilidad y no-shows',
        tips: [
          '**Fiabilidad** (columna) mide el % de no-shows del jugador. Por encima del **20 %** se marca **Alto riesgo** — verás también el total en el pie de la lista. Jugadores sin historial aparecen como *Nuevo*. Al crear o editar una reserva, si el cliente vinculado es de alto riesgo aparece un aviso en la sección **Cliente**, para verlo antes de confirmar.',
          'Cada **no-show** se cuenta a **todos los jugadores vinculados** al partido, no sólo a quien reservó. Si Carmen reserva para Antonio + 2 amigos y uno no aparece, el contador sube al jugador que falló — el filtro **Alto riesgo** del pie te lleva al verdadero reincidente.',
        ],
      },
    ],
    guides: [
      {
        title: 'Cómo buscar un cliente',
        steps: [
          'Escribe el **nombre, teléfono o email** en la barra de búsqueda.',
          'Los resultados se filtran **en tiempo real**.',
          'Usa el filtro **Personas / Grupos / Todos** para acotar.',
        ],
      },
      {
        title: 'Cómo exportar la lista de clientes',
        steps: [
          'Pulsa el icono de **exportar** (descarga) en la parte superior.',
          'Se descargará un archivo **CSV** con todos tus clientes.',
        ],
      },
      {
        title: 'Cómo completar el perfil de juego de un jugador',
        steps: [
          'Abre la ficha del jugador pulsando su fila.',
          'Despliega la sección **Juego**.',
          'Selecciona **Nivel** pulsando el botón de banda (Inic/Bajo/Medio/Alto/Avanz/Comp) o escribe un valor numérico 0–7 en el campo de texto. Es la escala estándar del pádel (NPRP).',
          'Rellena opcionalmente **Fecha de nacimiento** y **Licencia federativa**.',
          'Pulsa **Guardar** para persistir los cambios.',
        ],
      },
    ],
  },

  '/series': {
    title: 'Reservas recurrentes',
    description:
      'Gestiona **series de reservas semanales**: clases fijas, grupos de práctica y abonos de pista. Cada serie vincula un cliente y sus jugadores, y genera las reservas automáticamente.',
    sections: [
      {
        label: 'Cómo funciona la serie',
        tips: [
          'Una **serie recurrente** genera reservas automáticamente cada semana.',
          'Cancelar la serie **cancela todos los slots futuros** a partir de hoy.',
          'Cada reserva generada aparece en el **Calendario** con su etiqueta de serie.',
          'Si el día y hora elegidos quedan **fuera del horario habitual** del club verás un aviso en el formulario — la serie se crea igualmente.',
        ],
      },
      {
        label: 'Cliente y jugadores',
        tips: [
          'Usa la sección **Cliente** para vincular la serie a un contacto del CRM — igual que en reservas individuales. Busca por nombre o teléfono.',
          'La sección **Jugadores** admite hasta 4 jugadores con enlaces al CRM. Usa "vincular" para conectar cada fila a un perfil del CRM.',
          'Si el cliente **reserva para otra persona**, activa "Reserva para otra persona" — el jugador principal se puede rellenar por separado en Jugadores.',
        ],
      },
      {
        label: 'Cursos (con entrenador)',
        tips: [
          'Si el club tiene el **Rol Entrenador** activo, una serie puede ser un **curso**: elige el tipo **Clase**, asígnale un **entrenador** y un **nivel o grupo** (p. ej. «Iniciación», «Escuela infantil»).',
          'Los cursos aparecen con la etiqueta **Curso** en la lista y alimentan el **informe por entrenador** (pantalla Entrenadores). El **cupo de alumnos** por curso («**Alumnos por curso**» en Ajustes → Módulos → Entrenadores → Configurar) es **4** por defecto.',
        ],
      },
    ],
    guides: [
      {
        title: 'Cómo crear una serie recurrente',
        steps: [
          'Pulsa "**Crear**" en la parte superior.',
          'En **Cuándo**: elige la pista, cadencia, día de la semana, horario y duración.',
          'En **Cliente**: busca el contacto por nombre o teléfono, o marca "Anónima".',
          'En **Jugadores**: añade los jugadores con sus nombres y vínculos al CRM.',
          'En **Pago**: indica el precio por sesión (opcional).',
          'Pulsa "**Guardar**". Las reservas se generan automáticamente.',
        ],
      },
      {
        title: 'Cómo cancelar una serie',
        steps: [
          'Selecciona la serie en la lista.',
          'Pulsa "**Cancelar**" en el pie del panel y confirma.',
          'Todos los slots futuros quedan cancelados; el historial pasado se conserva.',
        ],
      },
    ],
  },

  '/staff': {
    title: 'Personal',
    description:
      'Gestiona los **miembros del equipo**: invita recepcionistas, gerentes y entrenadores, y ajusta sus **permisos** por rol.',
    tips: [
      'Cada **rol** tiene permisos distintos — abre **Permisos por rol** para verlos y, si eres Propietario, ajustarlos con un interruptor por permiso.',
      'Un miembro "**Pendiente**" fue invitado pero aún no ha iniciado sesión.',
      'El rol **Recepcionista** ve el bloque **Día a día**: Hoy, Calendario, Reservas, Recurrentes, Clientes, Ventas y Actividad. Cobra y **devuelve** (reembolsos por defecto), pero no ve el libro de Ingresos salvo que se lo des en Ajustes → Reglas.',
      'El rol **Gerente** suma el bloque **Gestión**: Pistas, Catálogo, Personal, Ingresos, Rendimiento, Novedades y Ajustes.',
      'El rol **Entrenador** (si activas el módulo en Ajustes → Módulos) es un acceso reducido: ve **solo su agenda de clases y sus alumnos**, con asistencia — **sin** facturación, ingresos ni el resto del club.',
    ],
    guides: [
      {
        title: 'Cómo invitar a un miembro del equipo',
        steps: [
          'Pulsa "**Invitar**" en la parte superior.',
          'Introduce el **email** de la persona que quieres invitar.',
          'Selecciona su **rol** (Recepcionista, Gerente, Entrenador…).',
          'Al iniciar sesión con ese email tendrá **acceso al club**.',
        ],
      },
      {
        title: 'Cómo cambiar el rol de un miembro',
        steps: [
          'Toca el miembro en la lista.',
          'Selecciona el **nuevo rol** en el desplegable.',
          'Los **permisos se actualizan** inmediatamente.',
        ],
      },
    ],
  },

  '/settings': {
    title: 'Ajustes',
    description:
      'La navegación de Ajustes está agrupada en tres bloques: **Club** (Perfil · Horario · Precios), **Operación** (Módulos · Reglas · Notificaciones) y **Tu cuenta** (Suscripción · Instalación). Suscripción solo es visible para el Dueño.',
    pages: [
      {
        id: 'club',
        label: 'Perfil',
        description:
          'Datos del club que aparecen en confirmaciones a jugadores y en la dirección compartida por WhatsApp / Maps. Solo el **Dueño** puede editar estos campos; el color del avatar también vive aquí.',
        sections: [
          {
            label: 'Datos de contacto y ubicación',
            tips: [
              'El **email y teléfono** del club aparecen en las confirmaciones automáticas a jugadores.',
              'Pulsa **"Buscar en Google Maps"** para localizar el club en el mapa: rellenamos dirección, ciudad, provincia, país y coordenadas en un solo paso.',
              'Cuando la dirección está enlazada con Google verás el sello **"Datos de Google Maps"** — significa que el placeId está guardado y los jugadores podrán abrir tu ficha desde la app.',
            ],
          },
          {
            label: 'Identidad y facturación',
            tips: [
              'El **NIF / CIF** es necesario para que las facturas que uPadelMatch te emite sean fiscalmente válidas.',
              'El **avatar** (iniciales con color) está al lado del nombre — pulsa para cambiar el color de marca; aparece en el encabezado del menú lateral.',
            ],
          },
          {
            label: 'Zona peligrosa y enlaces',
            tips: [
              'La **Zona peligrosa** al final permite solicitar el cierre del club. La eliminación se procesa con un periodo de gracia de **15 días**.',
              'Los cobros de tus reservas no están aquí: viven en **Ingresos**, en el menú lateral. Las facturas que uPadelMatch te emite están en **Suscripción**.',
            ],
          },
        ],
      },
      {
        id: 'modules',
        label: 'Módulos',
        description:
          'El catálogo de **capacidades activables** de tu club: Clientes, Ventas, Torneos y Entrenadores. Todas son **gratis** hoy. El **núcleo** (Reservas, Reservas recurrentes, Calendario, Pistas, Cobrar + Ingresos, Personal, Actividad, Rendimiento) está siempre incluido y no aparece como módulo. El **Monedero y bonos** no es un módulo aparte: es una opción dentro de **Clientes → Configurar**.',
        sections: [
          {
            label: 'Activar y desactivar',
            tips: [
              'Cada tarjeta muestra **qué incluye** el módulo y un interruptor. El borde **verde** = activo, **gris** = desactivado; la tarjeta no se mueve al cambiar. Solo el **Dueño** puede activarlos o desactivarlos.',
              '**Activar** es inmediato — no hay confirmación, el módulo queda disponible al instante.',
              '**Desactivar nunca borra datos.** Según el módulo, verás un aviso distinto antes de confirmar: sin nada pendiente se desactiva sin más; si un torneo está en juego se te avisa de qué sigue existiendo; con Entrenadores (accesos activos) confirmas antes de poder desactivar.',
            ],
          },
          {
            label: 'Configurar cada módulo',
            tips: [
              'Los módulos con ajustes propios muestran un botón **Configurar** junto al interruptor — abre el ajuste en un panel lateral.',
              '**Clientes**: Configurar abre el **Monedero y bonos** — el interruptor del saldo prepago + los **bonos de sesiones** (crear, editar, precio, caducidad). El monedero vive dentro de la ficha del cliente, por eso se configura aquí.',
              '**Entrenadores**: Configurar abre un panel lateral con el **cupo de alumnos por curso** (4 por defecto).',
              '**Ventas**: Configurar abre un panel lateral con quién puede usar líneas **"+ Otro"** fuera de catálogo, con un atajo a **Catálogo** para el resto de artículos.',
            ],
          },
          {
            label: 'Qué pasa con cada módulo al desactivarlo',
            tips: [
              '**Ventas** y **Clientes**: se ocultan sin más — nada se pierde, se reactivan cuando quieras.',
              '**Monedero** (dentro de Clientes): si tus jugadores tienen saldo o bonos pendientes, el dinero sigue siendo suyo — solo se oculta la vista hasta que reactives el interruptor.',
              '**Torneos**: si hay una categoría en juego, la sección se oculta para torneos nuevos pero no interrumpe los partidos en curso.',
              '**Entrenadores**: si hay entrenadores o cursos activos, pierden su rol de acceso al desactivar — sus datos no se borran.',
            ],
          },
        ],
      },
      {
        id: 'hours',
        label: 'Horario',
        description:
          'Horario semanal del club por **reglas**. Cada regla agrupa los días que comparten las mismas horas; casos avanzados como cierres overnight o festivos se ajustan por separado.',
        sections: [
          {
            label: 'Cómo funcionan las reglas',
            tips: [
              'Una **regla** = los días marcados (L M X J V S D) abren con el mismo horario.',
              'Si todos los días abren igual, **una regla con los 7 días** es suficiente.',
              'Para fines de semana distintos, añade una **segunda regla** con S y D y sus propias horas.',
              'Los días **no incluidos en ninguna regla** se consideran cerrados.',
            ],
          },
          {
            label: 'Casos avanzados',
            tips: [
              'Marca **Avanzado** en una regla para cerrar después de medianoche o partir el día en varios tramos.',
              'Los **Días especiales** (festivos, torneos) anulan la regla semanal para una fecha concreta.',
            ],
          },
          {
            label: 'Efecto en reservas existentes',
            tips: [
              'Los cambios solo afectan a **reservas futuras**: las existentes fuera del nuevo horario se mantienen.',
            ],
          },
        ],
        guides: [
          {
            title: 'Cómo abrir distinto entre semana y fin de semana',
            steps: [
              'En la primera regla, deja marcados solo **L M X J V** y pon su horario (p. ej. 09:00–22:00).',
              'Pulsa **+ Regla**.',
              'En la nueva regla, marca **S y D** y su horario propio (p. ej. 08:00–23:00).',
              'Pulsa **Guardar**.',
            ],
          },
          {
            title: 'Cómo cerrar más tarde de medianoche',
            steps: [
              'En la regla del día, activa el switch **Avanzado**.',
              'Marca **Cierra al día siguiente** en el tramo y elige la hora (p. ej. 02:00).',
              'Las reservas overnight aparecerán continuas en el calendario sin cortar a las 24:00.',
            ],
          },
          {
            title: 'Cómo añadir un día festivo',
            steps: [
              'Pulsa **+ Día** en la sección **Días especiales**.',
              'Elige la fecha y deja **Cerrado** activo.',
              'Pon una **etiqueta** opcional (p. ej. "Festivo local").',
              'Pulsa **Guardar**. La fecha aparecerá como aviso en Hoy y Calendario.',
            ],
          },
        ],
      },
      {
        id: 'precios',
        label: 'Precios',
        description:
          'Precio base del club y reglas por franja horaria, con una **precedencia** clara: pista > franja > base. Los servicios de reserva (luz, alquileres, fee de invitado…) se gestionan en **Catálogo**.',
        sections: [
          {
            label: 'Precedencia de precios',
            tips: [
              'El **precio base** es el precio por defecto cuando ninguna regla de franja o pista coincide.',
              'Las **reglas de franja** (pico / valle) tienen prioridad sobre el precio base.',
              'Los **precios por pista** tienen prioridad sobre las reglas de club — configúralos en la misma pestaña más abajo.',
              'Las reglas pueden tener una **ventana estacional** (Desde / Hasta): fuera de esas fechas la regla se salta automáticamente y se aplica la siguiente en prioridad.',
            ],
          },
          {
            label: 'Servicios y edición',
            tips: [
              'Los servicios que cobras en una reserva (luz, calefacción, alquiler de pala…) se configuran en **Catálogo**: crea un artículo de tipo *Servicio* y activa «Se puede añadir a una reserva».',
              'El **recálculo de precios** (si al cambiar un precio se reescriben las reservas futuras ya creadas) se ajusta en el último bloque de esta misma pestaña.',
              'Cada bloque de configuración aparece **plegado** con un resumen de lo que está activo — pulsa la cabecera para expandir y editar. Si tienes cambios sin guardar, el bloque se queda abierto.',
            ],
          },
        ],
        diagrams: [
          {
            caption: 'Precio por defecto: precedencia de reglas',
            source: PRICING_PRECEDENCE_DIAGRAM,
          },
        ],
      },
      {
        id: 'policies',
        label: 'Reglas',
        description:
          'Define las **reglas core de tu club**: quién elimina reservas, reembolsa o ve ingresos. El **recálculo de precios** vive ahora en **Ajustes → Precios**, junto al resto de la configuración de precios. Activar módulos (Monedero, Rol Entrenador, Torneos…) y **configurarlos** (bonos, alumnos por curso, líneas "+ Otro") se hace en **Ajustes → Módulos**, en la tarjeta de cada módulo. Cada panel se abre al pulsar su cabecera.',
        sections: [
          {
            label: 'Reservas y cancelaciones',
            tips: [
              '**Eliminación de reservas**: quién puede borrar una reserva recién creada (≤24h) sin dejar rastro. Por defecto Propietario y Gerente; el Recepcionista solo cancela. Si tenía pagos o estaba cancelada, verás un aviso antes de confirmar.',
              '**Reservas anónimas**: si el mostrador puede crear reservas sin vincular un cliente. Desactívalo para que toda reserva tenga identidad (mejor para cuadrar caja).',
              '**Margen de presentación**: minutos de cortesía tras la hora de inicio antes de marcar «sin presentar» en Acciones pendientes (15 min por defecto).',
            ],
          },
          {
            label: 'Cobros y caja',
            tips: [
              '**Reembolsos** y **Visibilidad de ingresos** se controlan por rol. En un club pequeño el mostrador cobra y devuelve; puedes reservar ambas a gestión.',
              '**Motivo en pagos parciales**: exige una nota cuando un cobro deja la reserva muy por debajo del total — deja rastro de por qué.',
            ],
          },
        ],
      },
      {
        id: 'notifications',
        label: 'Notificaciones',
        description:
          '**Emails automáticos** y **avisos push** que envía la aplicación en nombre del club, con un **interruptor** por destinatario/tipo de email y por dispositivo — actívalos sólo si quieres ese aviso.',
        tips: [
          '**A los jugadores**: confirmación, cancelación y reprogramación de reservas. Llegan al email del jugador que figura en la reserva — si no hay email, no se envía.',
          '**A ti (propietario)**: aviso por cada reserva nueva o resumen diario a las 22:00. Llegan al email de tu cuenta uPadelMatch.',
          '**Avisos push por dispositivo**: recibe un aviso al entrar o cancelarse una reserva aunque no mires la pantalla. Cada dispositivo se activa por separado; en **iPhone/iPad** hay que **instalar la app** primero.',
          'Por defecto todos los interruptores están **apagados**. Al claim de la cuenta no salen emails automáticamente.',
          'La **verificación de email** y el **restablecimiento de contraseña** son emails de seguridad y no se pueden desactivar.',
        ],
      },
      {
        id: 'subscription',
        label: 'Suscripción',
        description:
          'Estado del club, cronograma de la suscripción, **plan** elegido y facturas que uPadelMatch te emite. Si llegaste por un enlace de previsualización, aquí también vive **Reclamar club**.',
        sections: [
          {
            label: 'Reclamar y estado del club',
            tips: [
              '**¿Llegaste por un enlace de demostración?** Esta pantalla es donde reclamas el club: introduce tu email y enviaremos la invitación al propietario. Cuando la acepte, eliminamos los datos de demostración y arranca tu prueba de 90 días.',
              'Si nadie reclama el club en **15 días**, lo eliminaremos automáticamente — no se queda colgando.',
              'El **estado** del club aparece arriba: Maqueta · En prueba · Activa · Pago pendiente · Cerrada. Cada estado tiene su propio cronograma debajo.',
              'El **cronograma** muestra qué pasará si no haces nada — fin de prueba, periodo de gracia, cierre. Las fechas se calculan a partir de los datos de tu suscripción.',
            ],
          },
          {
            label: 'Planes y precios',
            tips: [
              'El **resumen de pago** usa el número real de **pistas activas** (no el guardado en la suscripción). Si difieren, verás un aviso ⚠️ — se reconcilia en el siguiente ciclo.',
              'Durante la **prueba** no se cobra nada. Lo cobraremos al fin de la prueba, con el número de pistas activas en ese momento.',
              `La **Tarifa Fundador** (€${SubscriptionPricing.partnerPerCourt}/pista/mes) es privada y por invitación — si tu club la tiene, aparece como plan actual con un sello.`,
            ],
          },
          {
            label: 'Facturas',
            tips: [
              'Las **facturas** de uPadelMatch (no las de tus reservas) están al final de esta pestaña. Los cobros de tus reservas están en **Ingresos**, en el menú lateral.',
            ],
          },
        ],
        guides: [
          {
            title: 'Cómo reclamar el club desde una demostración',
            steps: [
              'En **Reclamar club**, escribe el email del propietario del club.',
              'Pulsa **Enviar**. Le enviamos una invitación por correo.',
              'Cuando el propietario acepte la invitación y cree su cuenta, **eliminaremos los datos de demostración** y arrancará tu **prueba de 90 días**.',
              'Mientras la invitación está pendiente, los datos de demostración siguen visibles. Si pasan 15 días sin que nadie reclame, el club se eliminará automáticamente.',
            ],
          },
          {
            title: 'Cómo elegir el plan durante la prueba',
            steps: [
              'Mira los **Planes** disponibles y compara totales por tu número de pistas.',
              'Pulsa **Elegir mensual** o **Elegir anual** — se anotará en tu suscripción.',
              'No se cobra nada hasta el **fin de la prueba**; verás la fecha y el importe estimado en "Próximo cargo".',
              'Cuando se cobre el primer mes, recibirás la primera factura por email y aquí abajo.',
            ],
          },
          {
            title: 'Cómo añadir tu método de pago',
            steps: [
              'Pulsa **"Añadir método de pago"** en la parte inferior del panel.',
              'Se abre un panel lateral con un **formulario de tarjeta** seguro; nunca almacenamos tu número.',
              'Introduce tu **NIF/CIF** del club — necesario para emitir facturas.',
              'Pulsa **"Confirmar y guardar"**. Si el banco lo pide, completa la verificación 3DS en tu móvil.',
              'No se cobra nada todavía si estás en **prueba**: el primer cargo se hace al fin de la prueba.',
            ],
          },
          {
            title: 'Cómo cambiar tu plan después de la prueba',
            steps: [
              'En **Planes**, pulsa **Elegir** en el plan al que quieras cambiar.',
              'Verás el **importe prorrateado**: el plan anual cobra €{año} hoy y abona los días restantes del mes en curso.',
              'Confirma. La factura se genera inmediatamente y aparece en **Facturas**.',
              'Para volver al mensual desde anual: misma operación, sin cargos hasta el próximo periodo.',
            ],
          },
          {
            title: 'Cómo cancelar la suscripción',
            steps: [
              'Pulsa **"Cancelar"** en la parte inferior del panel.',
              'Mantendrás **acceso completo hasta el final del periodo** ya pagado — no se reembolsa el periodo en curso.',
              'Verás un aviso "**Cancelación programada**" hasta esa fecha; puedes **reactivar** en cualquier momento antes con un clic.',
              'Pasada la fecha, el club queda **cerrado** y los datos se conservan **90 días**.',
            ],
          },
          {
            title: 'Qué pasa si no añado método de pago',
            steps: [
              'Al **fin de la prueba**, el club pasa a **Pago pendiente** (solo lectura).',
              'Tienes **15 días de gracia** para añadir tarjeta y reactivar.',
              'Pasados los 15 días, el club se **cierra**. Tus datos se conservan **90 días** por si quieres volver.',
            ],
          },
        ],
      },
      {
        id: 'install',
        label: 'Instalación',
        description:
          'Instala uPadelMatch como **app** en tu dispositivo y consulta qué equipo de recepción funciona mejor. Carga más rápido, no necesitas el navegador y aparece como icono en la pantalla de inicio.',
        sections: [
          {
            label: 'Cómo instalar según dispositivo',
            tips: [
              'Si ya tienes la app instalada verás el sello **App instalada** y nada más — no hay que hacer nada.',
              'En **Android Chrome / Edge** y en **Chrome / Edge de escritorio** aparece un botón **Instalar ahora** que lanza el diálogo nativo del navegador.',
              'En **iPhone / iPad con Safari** la instalación se hace desde el botón **Compartir → Añadir a pantalla de inicio**. Los pasos están en esta pestaña.',
              'En **Mac Safari** o **Firefox** no se puede instalar como app. Cambia a **Chrome / Edge** para tener el icono en escritorio.',
            ],
          },
          {
            label: 'Píldora, recomendaciones y versión',
            tips: [
              'Si descartas la **píldora "Instala la app"** del lateral, no volverá a aparecer en este dispositivo. Siempre puedes volver aquí.',
              'La sección **Recomendaciones de equipo** (debajo del instalador) ordena qué dispositivos van mejor para operar el día — desde el ordenador de recepción ideal hasta lo que conviene evitar.',
              'En el panel **Acerca de** verás la **versión** instalada — útil cuando reportes una incidencia.',
            ],
          },
        ],
      },
    ],
  },

  '/activity': {
    title: 'Actividad',
    description:
      'Historial completo de todas las **acciones del club**: reservas, pagos, cambios de personal y configuración. Filtra y exporta para auditoría.',
    tips: [
      'Filtra por **fecha, persona o tipo de acción**.',
      '**Exporta a CSV** para auditoría o contabilidad.',
      'Cada entrada muestra **quién hizo qué** y la hora **al segundo**, para ordenar acciones seguidas del mismo minuto.',
    ],
  },

  '/analytics': {
    title: 'Rendimiento',
    description:
      'Indicadores del negocio por **ventana de tiempo** (día / semana / mes): KPIs, mapa de calor de ocupación y desglose de ingresos. Exporta a CSV para tu contabilidad.',
    sections: [
      {
        label: 'KPIs y ventana temporal',
        tips: [
          'El KPI **Ingresos cobrados** encabeza la página: responde de un vistazo "¿cuánto he ingresado este periodo?", con la variación frente al periodo anterior.',
          'Cambia la **ventana temporal** arriba a la derecha para recalcular todos los KPIs.',
        ],
      },
      {
        label: 'Mapa de calor y desglose',
        tips: [
          'El **mapa de calor** muestra ocupación promedio por pista y hora; los bloques más oscuros concentran la demanda.',
          '**Ingresos por día de la semana** revela qué días ganan más — útil para ajustar precios y turnos de personal (más claro en ventanas de 30/90 días).',
          '**Demanda perdida** suma las reservas canceladas y las ausencias por día: vigílala para detectar a tiempo ingresos que se escapan.',
          '**Ingresos por origen** (al final, a la izquierda) reparte lo cobrado entre **Reservas** (alquiler de pista) y **Ventas** (extras de mostrador) — el desglose de alto nivel de "¿de dónde viene mi dinero?". En un club de pádel las reservas suelen ser el grueso y las ventas, ingresos complementarios.',
          '**Ventas por producto** (al final, a la derecha) muestra el ranking de ingresos por artículo del catálogo — pelotas, alquiler, bebidas — para la ventana seleccionada. Solo cuenta ventas cobradas; las pendientes no aparecen.',
        ],
      },
      {
        label: 'Exportar, alertas e IA',
        tips: [
          '**Exporta a CSV** para contabilidad o análisis externo.',
          '**Tasa de no-show > 15 %**: revisa la política de cancelación o la ventana de aviso.',
          'Si tu club tiene **Análisis con IA** activo, pulsa el botón junto al selector de periodo para recibir un resumen con recomendaciones en español.',
        ],
      },
    ],
    guides: [
      {
        title: 'Cómo interpretar los KPIs principales',
        steps: [
          '**Ingresos cobrados**: suma de reservas cobradas (efectivo + tarjeta). Excluye pendientes y canceladas.',
          '**Ocupación media**: slots reservados ÷ slots totales disponibles. Por encima del **50 %** sostenido el club cubre costes fijos en la mayoría de configuraciones.',
          '**Tasa no-show**: no-shows ÷ (no-shows + presentados). Meta: < **10 %**.',
          '**€ por pista-hora** (en la tabla **Detalle por pista**): ingresos ÷ horas jugadas. Útil para comparar pistas entre sí y detectar franjas infravaloradas.',
        ],
      },
      {
        title: 'Cómo leer el mapa de calor',
        steps: [
          'Cada celda es una **franja horaria × día de la semana** en la ventana seleccionada.',
          'Los bloques más **oscuros** concentran más ocupación — candidatos para precio premium.',
          'Los bloques **pálidos** en franjas que deberían llenarse señalan hueco para promos o clases.',
        ],
      },
      {
        title: 'Cómo interpretar Ventas por producto',
        steps: [
          'La tarjeta **Ventas por producto** muestra los artículos del catálogo ordenados por **ingresos cobrados** en el periodo.',
          'Cada barra es proporcional al producto que más vendió — útil para detectar qué artículo genera más ancillary revenue.',
          'La columna **%** indica la fracción del total de extras que aporta ese artículo.',
          'Las ventas libres ("+ Otro") se agrupan en "Otros / varios". Si hay más de 6 productos, los restantes se pliegan en **Otros (N)** con la suma total.',
          'Si la tarjeta no aparece, no hubo ventas cobradas en la ventana — amplía el rango o registra las primeras extras desde Ventas.',
        ],
      },
      {
        title: 'Cómo detectar fugas de ingresos',
        screenTarget: '/ingresos',
        ctaLabel: 'Ir a Ingresos',
        steps: [
          'Compara **Ingresos** con **Horas jugadas**: si suben horas pero no ingresos, hay cobros pendientes.',
          'Revisa **Pago pendiente** en Ingresos para saldar la diferencia.',
          'Cruza con la **Tasa no-show**: si es alta y no cobras la política, estás perdiendo ingresos.',
        ],
      },
    ],
  },

  '/onboarding': {
    title: 'Configura tu club',
    description:
      'Asistente de 5 pasos para dejar el club listo: **Tu club** (perfil), **Pistas**, **Horario**, **Datos** de prueba y un resumen final **Listo**. Todo es editable después desde **Ajustes** y el menú lateral.',
    sections: [
      {
        label: 'Qué configura el asistente',
        tips: [
          'Las **pistas** son obligatorias para aceptar reservas — configura al menos una.',
          'El **horario** se aplica por defecto a todas las pistas; puedes sobreescribirlo por pista después.',
          'El paso **Datos** es opcional: puedes saltarte el seed y arrancar con el club vacío. Útil cuando quieres ver el aspecto del calendario con reservas de ejemplo.',
        ],
      },
      {
        label: 'Después y ayuda',
        tips: [
          'Todo lo que configures ahora se puede **editar más tarde** desde **Ajustes** (perfil, horario, precios) y desde **Pistas** y **Personal** en el menú lateral.',
          'El equipo de recepción se invita después desde **Personal** (menú lateral) — el onboarding no crea cuentas de staff.',
          'Si necesitas ayuda, pulsa **?** en cualquier momento para volver aquí.',
        ],
      },
    ],
    guides: [
      {
        title: 'Paso 1 · Tu club',
        steps: [
          '**Nombre**: cómo se mostrará a jugadores y personal.',
          '**Ciudad** y **zona horaria**: importante para que los horarios de reserva sean correctos.',
          '**Teléfono** y **email**: aparecen en las confirmaciones automáticas a jugadores.',
        ],
      },
      {
        title: 'Paso 2 · Pistas',
        steps: [
          'Dale **nombre corto** a cada pista (Pista 1, Central, Cristal 3…).',
          'Marca **tipo** (cristal / malla / muro) y **ubicación** (indoor / outdoor).',
          'Puedes añadir más pistas o editarlas después desde **Pistas** en el menú lateral.',
        ],
      },
      {
        title: 'Paso 3 · Horario',
        steps: [
          'Define el horario semanal del club por **reglas**: cada regla agrupa días con las mismas horas.',
          'Si abres igual los 7 días, una sola regla con los 7 días marcados es suficiente.',
          'Ajustes finos (fines de semana distintos, cierre overnight, festivos) se gestionan después en **Ajustes → Horario**.',
        ],
      },
      {
        title: 'Paso 4 · Datos (opcional)',
        steps: [
          'Pulsa **Crear datos de prueba** para sembrar reservas + jugadores ficticios y ver el calendario poblado desde el primer minuto.',
          'O salta este paso: el club arranca vacío y empiezas a registrar reservas reales directamente.',
          'Los datos sembrados se pueden borrar luego desde **Clientes** y **Reservas** sin afectar la configuración.',
        ],
      },
      {
        title: 'Paso 5 · Listo',
        steps: [
          'Resumen de lo configurado. Pulsa **Entrar al club** para empezar.',
          'Aterrizas en **Hoy** — el panel de operaciones del día.',
          'Invita al equipo desde **Personal** y revisa los siguientes pasos sugeridos en el botón flotante **Novedades** de la esquina inferior derecha.',
        ],
      },
    ],
  },

  '/accept-invite': {
    title: 'Aceptar invitación',
    description:
      'Te han invitado a formar parte del equipo de un club. Al aceptar, tu cuenta queda asociada al club con el **rol asignado** (Recepcionista, Gerente o Dueño).',
    tips: [
      'El rol **Recepcionista** ve lo necesario para el día a día (Hoy, Calendario, Reservas, Clientes, Ventas) — sin acceso a Ajustes ni a facturación.',
      'El rol **Gerente** suma acceso al bloque **Gestión**: Pistas, Catálogo, Personal, Ingresos, Rendimiento y Ajustes.',
      'El rol **Dueño** incluye todo lo anterior más **Suscripción** y facturación.',
      'Si aceptas por error, quien te invitó puede **revocar el acceso** desde Personal.',
    ],
    guides: [
      {
        title: 'Qué pasa cuando acepto',
        steps: [
          'Tu cuenta queda **vinculada al club** con el rol indicado.',
          'Al iniciar sesión verás el club por defecto; puedes **cambiarlo** desde la barra superior si perteneces a varios.',
          'Tu nombre aparecerá en **Personal** y tus acciones quedarán registradas en Actividad.',
        ],
      },
    ],
  },

  '/coaches': {
    title: 'Entrenadores',
    description:
      'Informe de actividad **por entrenador**: clases impartidas, horas e ingresos de esas clases en la ventana elegida. Se nutre de las reservas de tipo **Clase** con un entrenador asignado.',
    sections: [
      {
        label: 'Cómo se llena',
        tips: [
          'Cada fila resume a un entrenador que impartió **al menos una clase** en el periodo. Cambia la **ventana** (7/30/90 días) arriba a la derecha.',
          'Los datos vienen de reservas de tipo **Clase** con entrenador. Para que aparezcan, crea la reserva como **Clase** y elige el entrenador en el formulario.',
          'Las clases **canceladas** no cuentan (no se impartieron). **Ingresos** es el precio de las clases del entrenador en el periodo.',
        ],
      },
      {
        label: 'Estimado a pagar',
        tips: [
          'Es un **cálculo orientativo** = la tarifa del entrenador (Personal → ficha) × su actividad (ingresos, horas o clases según el modelo). **No es la liquidación final** — el cálculo automático de nóminas llegará más adelante.',
          'En **alquiler de pista** el entrenador paga al club, así que el importe sale en **gris** (el club cobra, no paga).',
          'Si un entrenador no tiene **modelo de pago** definido, el estimado aparece como «—». Defínelo en su ficha de Personal.',
        ],
      },
    ],
  },

  '/tournaments': {
    title: 'Torneos',
    description:
      'Organiza torneos con una o varias **categorías** — cada una con su propio **formato** (Americano, Mexicano, parejas fijas…), sus jugadores y su **cuadro** — inscribe jugadores y lleva el **resultado de cada partido** con una **clasificación en directo**.',
    sections: [
      {
        label: 'Crear un torneo',
        tips: [
          'Añade una o varias **categorías** (p. ej. «Masculina 2ª» y «Femenina B»): cada una elige **formato**, **pistas**, **puntuación** —a **puntos** (suman al total o «primero a»), a **tiempo** o a **sets** (al mejor de 3/5, con juegos y desempate)— y **rondas**.',
          'Formatos implementados hoy: **Americano** y **Americano mixto** (rotan pareja cada ronda; el mixto exige un jugador de cada sexo por pareja), **Mexicano** (empareja por clasificación cada ronda), **Americano por parejas** y **Round-robin** (parejas fijas, todos contra todos), **Eliminatoria** (cuadro directo, con opción de **consolación** para quien cae en la 1ª ronda y de **3er/4º puesto**) y **Grupos + cuadro** (fase de grupos y luego eliminatoria entre los clasificados). Solo la **Liga interna** por semanas queda como «próximamente».',
          'Con un número de jugadores que no es múltiplo de 4, algunos **descansan** cada ronda, rotando de forma equilibrada — lo mismo aplica al Americano mixto cuando los sexos no están igualados.',
          'Abre el torneo y entra en **Ajustes** (menú lateral): ahí editas los datos del evento y cada categoría tiene un botón **Duplicar** que copia toda su configuración (formato, pistas, puntuación, cupo, cuota…) en una categoría nueva y vacía — útil para repetir el mismo montaje en otro nivel o sexo.',
          'En la puntuación por **sets** puedes activar **punto de oro** (el punto de la igualada decide el juego) — es solo informativo, se muestra en la etiqueta del partido.',
        ],
      },
      {
        label: 'Inscripción y cuadro',
        tips: [
          'En cada categoría, busca jugadores del **CRM** o añádelos por nombre, y pulsa **Generar cuadro**. En **Americano mixto**, elige el **sexo** de cada jugador al añadirlo (o corrígelo después con un toque en su chip).',
          'En **Americano**/**Americano mixto**/**Round-robin**/**Americano por parejas** se genera la rotación completa de una vez. En **Mexicano** se genera solo la primera ronda por nivel; el resto se generan desde los resultados. **Eliminatoria** genera el cuadro directo desde el nivel de las parejas. **Grupos + cuadro** juega primero la liguilla de cada grupo y, al terminarla, genera el cuadro entre los mejores de cada grupo.',
          'Antes de generar una **Eliminatoria** (o el cuadro de una fase de grupos), puedes reordenar a mano las **cabezas de serie** si no quieres seguir el orden automático por nivel — el botón **Nivel** deshace el cambio.',
          'Ajusta el número de **rondas** antes de generar — para grupos grandes se juegan «americanos cortos» de **hasta 9 rondas** en vez de la rotación completa.',
        ],
      },
      {
        label: 'Jugar y clasificación',
        tips: [
          'En cada partido introduce el marcador de los dos equipos y pulsa **Guardar** — la **clasificación de la categoría** se recalcula al instante.',
          'Los formatos individuales/Americano **acumulan puntos** (jugador o, en Americano por parejas, la pareja fija); **Round-robin** clasifica como una **liga** (victoria/empate/derrota). Puedes **corregir** un resultado cuando quieras. En caso de **empate**, el desempate sigue el orden que muestra el icono **ⓘ** de la clasificación (en liga: puntos → **enfrentamiento directo** → diferencia de juegos).',
          'Al terminar una categoría, pulsa **Finalizar** — el torneo se marca como finalizado cuando todas sus categorías lo están. Torneos se activa en **Ajustes → Módulos** (está en pruebas — Beta).',
        ],
      },
      {
        label: 'Imprevistos durante el torneo',
        tips: [
          'El menú **⋯** de cada partido marca un **walkover** (ausencia — el rival gana limpio), **reasigna pista/hora** o **vacía el resultado** para volver a introducirlo.',
          'Con la categoría **en juego**, pulsa **Jugadores** para **sustituir** a alguien, darlo de **baja** (sus partidos pendientes pasan a walkover) o **añadir** un jugador de última hora sin perder los resultados ya guardados.',
          'Tras añadir o dar de baja jugadores, pulsa **Regenerar** en Partidos para que el cuadro pendiente refleje la plantilla actual — las rondas ya jugadas no se tocan. Lo verás en **Americano**, **Americano mixto**, **Americano por parejas** y **Round-robin**; en **Grupos + cuadro** cada grupo tiene el suyo, para no tocar los demás. **Mexicano** y **Eliminatoria** no lo necesitan: el Mexicano empareja cada ronda con la plantilla del momento y el cuadro se recalcula solo desde los resultados.',
          '**Añadir ronda** amplía un Americano (o un Americano mixto) más allá de su objetivo inicial — los formatos de parejas juegan las jornadas que salen del sorteo, así que ahí no aparece. **Reabrir** devuelve la categoría a inscripción para reordenar la plantilla con calma.',
          'El botón **Emparejar** de cada ronda te deja intercambiar a dos jugadores entre dos partidos distintos sin tocar los ya jugados — útil si un cruce ha quedado descompensado.',
          'El botón **Proyector** de la cabecera muestra la clasificación (o el cuadro) a pantalla completa, ideal para proyectarla en una pantalla o TV del club.',
        ],
      },
    ],
  },

  '/ingresos': {
    title: 'Ingresos',
    description:
      'Libro de **movimientos de dinero**: una línea por cada cobro o reembolso, con su fecha y método. Lo que aún no se ha cobrado se gestiona en **Reservas** — este libro es solo de lectura.',
    sections: [
      {
        label: 'Qué es cada línea',
        tips: [
          'Cada línea es **un pago** (o un reembolso, en rojo y con signo −), no una reserva. Una reserva pagada por 4 jugadores en días distintos aparece como 4 líneas.',
          'La columna **Fecha pago** es cuándo entró el dinero; **Reserva** muestra la hora de la pista y a quién corresponde el pago.',
          'Si el club tiene el **Monedero** activo: gastar **saldo o un bono** aparece aquí como un pago más (método «Otro»), porque es ingreso al consumirse. Una **recarga de saldo**, en cambio, **no** sale en este libro — es dinero prepago (un **pasivo**: «saldo en circulación») y sólo se vuelve ingreso al gastarse.',
        ],
      },
      {
        label: 'Corregir y pendiente',
        tips: [
          '¿Cobrar por error? La corrección se hace **desde la reserva**, no aquí: abre la reserva (toca su fila en el libro, o búscala en **Reservas**). En su detalle, **Reembolsar** registra la devolución como una línea negativa, y **Eliminar movimiento** borra el cobro como si nunca hubiera ocurrido (≤ 24 h) dejando una copia frozen para auditoría. El libro de Ingresos es **solo de lectura**.',
          'El enlace **"Pendiente · en Reservas"** del pie te lleva a lo que falta por cobrar — ahí está el botón **Cobrar**.',
        ],
      },
      {
        label: 'Filtrar y exportar',
        tips: [
          'El período filtra por **fecha de reserva**; las líneas se **agrupan por fecha de pago**. Útil para cuadrar la caja del día.',
          '**Exporta a CSV** cualquier período para tu gestor — una fila por movimiento.',
        ],
      },
      {
        label: 'Facturas de la suscripción',
        tips: [
          'Las facturas que uPadelMatch te emite por tu plan están en **Club → Suscripción** (solo Dueño).',
        ],
      },
    ],
  },

  '/select-club': {
    title: 'Clubes',
    description:
      'Selecciona el **club** al que quieres acceder. Los administradores de plataforma ven todos los clubes y pueden entrar en cualquiera.',
    tips: [
      'Como **administrador**, usa la tabla para buscar, filtrar y entrar en cualquier club.',
      'El botón **Entrar** te impersona en ese club — los cambios se aplican al club seleccionado.',
      `El filtro **Partner** muestra solo clubes con tarifa fundador (€${SubscriptionPricing.partnerPerCourt}/pista/mes). El programa fundador es privado y por invitación. El botón verde **Partner** en cada fila abre el diálogo de confirmación para conceder o retirar la tarifa.`,
    ],
  },

  '/admin/clubes': {
    title: 'Clubes (admin)',
    description:
      'Tabla maestra de todos los clubes de la plataforma con su estado de suscripción y de actividad.',
    tips: [
      'Cada fila muestra **MRR · Plan · Última actividad · Estado**. MRR es el ingreso recurrente mensual que aporta ese club; "—" significa que no está pagando (maqueta, prueba, exento o cerrado).',
      'El estado **En prueba** lleva el contador de días; **Activo · Cancelación al fin del periodo** se marca en naranja para verlo de un vistazo.',
      'Pulsa una fila para abrir el panel lateral. Diseño plano en tres zonas: **ESTADO** (estado del club + modo demo + datos de ejemplo), **SEGUIMIENTO DE LA INVITACIÓN** (tokens + timeline de actividad + recorrido en demo), **Avanzado** (único desplegable — auditoría + flags de plataforma + auto-eliminación de maqueta). Zonas 1 y 2 siempre visibles, sin colapsar.',
      'La sección **Avanzado → Auditoría** lista las últimas operaciones admin sobre el club (qué se cambió, quién y cuándo) — útil para reconstruir un toggle accidental.',
    ],
  },

  '/admin/salud': {
    title: 'Salud de la plataforma',
    description:
      'Visión transversal: cuántos clubes hay, cuántos pagan, MRR agregado, configuración pendiente y estado de la versión / SW.',
    tips: [
      'Las **tarjetas KPI** suman los clubes en cada estado y el **MRR** total. "Pagando" excluye maquetas, pruebas, exentos y suscripciones cerradas.',
      'El panel **Estado de configuración** lista cinco verificaciones: NIF, nombre, dirección, pistas activas y reglas de precio. Un club con cualquiera de ellas vacía no puede facturar bien.',
      'La sección **Versión** detecta si el bundle servido coincide con el publicado en Firestore; cuando hay desfase, pulsa **Recargar** para forzar la actualización.',
      'El enlace de **Sentry** filtra automáticamente por entorno (prod / dev) y últimas 24 h — abre solo los errores que importan ahora.',
      'El panel **Integridad de datos** registra el *drift* de esquema detectado en esta sesión (campos que `lenient()` tuvo que corregir al leer). Está vacío al cargar; se llena al navegar por las pantallas afectadas. Para el histórico cross-sesión usa el enlace de Sentry de ese mismo panel.',
    ],
  },

  '/product': {
    title: 'Producto',
    description:
      'Lo que tu club puede hacer **hoy**, lo que llega después y dónde **votar** o sugerir mejoras.',
    tips: [
      'Priorizamos lo más votado entre todos los clubes: tu **voto** cuenta.',
      'Las **sugerencias** las revisa el equipo cada semana.',
      'Pulsa **Novedades** (junto al número de versión) para ver qué ha mejorado en cada actualización.',
    ],
  },

  '/preview': {
    title: 'Modo demostración',
    description:
      'Estás viendo una **maqueta de tu club** con datos de ejemplo, para que veas cómo funcionará por dentro antes de empezar a usarlo de verdad. Es **solo lectura** hasta que reclames el club.',
    tips: [
      'Es **solo lectura**: puedes navegar todas las pantallas, pero los botones de crear, modificar y borrar están deshabilitados.',
      'Las **reservas, jugadores y actividad** que ves son ficticios — los borraremos al activar tu prueba.',
      'El enlace caduca; si te pasa de plazo, **pide al propietario un enlace nuevo**.',
      'Cuando lo veas claro, pulsa **Reclamar** desde Suscripción: te enviaremos un email para crear tu cuenta y arrancar los **90 días de prueba gratis**.',
    ],
    guides: [
      {
        title: 'Cómo paso de demo a prueba real',
        steps: [
          'Pulsa **Reclamar** en el banner superior o en Configuración → Suscripción.',
          'Indica el **email del propietario** del club. Recibirá un email con un enlace para crear su cuenta.',
          'Al aceptar, **borramos los datos de ejemplo** y arrancamos los 90 días de prueba con tu club vacío y listo.',
          'Los enlaces de demostración dejan de funcionar tras la activación — desde ese momento accedes con tu propia cuenta.',
        ],
      },
    ],
  },
}

/**
 * Resolve help content for a route. Returns null if the route has no entry
 * (caller decides whether to hide the `?` button or show a generic message).
 */
export function helpFor(pathname: string, tier: HelpRoleTier | null = null): HelpContent | null {
  const entry = REGISTRY[pathname]
  if (!entry) return null
  return typeof entry === 'function' ? entry(tier) : entry
}
